import { fromEvent, Observable } from 'rxjs';
import { filter, share, tap } from 'rxjs/operators';
import { ContentEditableElement } from '../../editors/base/contentEditableEditor';
import { findNextNonWhitespaceSibling, findPreviousNonWhitespaceSibling } from '../../helpers/dom/node';
import { createPointWithEndOfRange, createPointWithStartOfRange } from '../../helpers/dom/point';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { ElementSelectionService } from './elementSelectionService';
import { createElementEffectService } from './elementService';

/** Allows the Tab key to change focus only from the start or end of the editor. */
export function createContentEditableTabKeyService(
  element$: Observable<ContentEditableElement | null>,
  selectionService: ElementSelectionService,
) {
  return createElementEffectService(element$, element =>
    fromEvent<KeyboardEvent>(element, 'keydown').pipe(
      ignoreDuringCompositions(element),
      filter(event => event.key === 'Tab'),
      tap(event => {
        // Allow default behavior only if we know we're at the start or end.
        const { selection } = selectionService;
        if (selection && isAtStartOrEndOfContentEditable(element, selection.range, event)) {
          return;
        }

        event.preventDefault();
      }),
      share(),
    ),
  );
}

function isAtStartOrEndOfContentEditable(
  element: ContentEditableElement,
  range: Range,
  tabKeyEvent: KeyboardEvent,
): boolean {
  return tabKeyEvent.shiftKey
    ? isAtStartOfContentEditable(element, range)
    : isAtEndOfContentEditable(element, range);
}

function isAtStartOfContentEditable(element: ContentEditableElement, range: Range): boolean {
  return (
    range.collapsed &&
    createPointWithStartOfRange(range).isAtStartOfNode(
      element,
      node => !findPreviousNonWhitespaceSibling(node),
      element,
    )
  );
}

function isAtEndOfContentEditable(element: ContentEditableElement, range: Range): boolean {
  return (
    range.collapsed &&
    createPointWithEndOfRange(range).isAtEndOfNode(
      element,
      node => !findNextNonWhitespaceSibling(node),
      element,
    )
  );
}
